import React, { useEffect, useState } from "react";
import { InputNumber, List, Modal, Typography } from "antd";
import { HardwareReplacementCandidateDto } from "../../../interfaces/OrderDetail";
import { formatCurrency } from "./quoteVersionUtils";
import ModalSaveCancelButtons from "../../../components/ModalSaveCancelButtons";
import Flex from "../../../components/Flex";
import { Skeleton } from "../../../components/Skeleton";
import { useIsDarkModeEnabled } from "../../../hooks/useIsDarkModeEnabled";
import { css } from "@emotion/css";

interface HardwareReplacementModalProps {
  open: boolean;
  currentItem: string | null;
  currentQuantity: number | null;
  candidates: HardwareReplacementCandidateDto[];
  loading: boolean;
  onCancel: () => void;
  onConfirm: (newItem: string, quantity: number) => void;
}

const lightBackgroundColor = "#eaeaea";
const lightSelectableItemClass = css`
  &:hover {
    background-color: ${lightBackgroundColor};
    border-radius: 8px;
    transition: background-color 0.3s ease;
  }
`;

const darkBackgroundColor = "#5b6475";
const darkSelectableItemClass = css`
  &:hover {
    background-color: ${darkBackgroundColor};
    border-radius: 8px;
    transition: background-color 0.3s ease;
  }
`;

function SelectableCandidateRow({
  candidate,
  selected,
  onSelect,
}: {
  candidate: HardwareReplacementCandidateDto;
  selected: boolean;
  onSelect: () => void;
}) {
  const darkModeEnabled = useIsDarkModeEnabled();
  return (
    <List.Item
      className={darkModeEnabled ? darkSelectableItemClass : lightSelectableItemClass}
      onClick={onSelect}
      style={{
        width: "100%",
        padding: "8px",
        borderRadius: "8px",
        cursor: "pointer",
        ...(selected
          ? { backgroundColor: darkModeEnabled ? darkBackgroundColor : lightBackgroundColor }
          : {}),
      }}
    >
      <Flex justify="space-between" align="center" style={{ width: "100%" }} gap="8px">
        <Typography className="line-clamp-1" title={candidate.description}>
          {candidate.description}
        </Typography>
        <Typography style={{ flexShrink: 0 }}>{formatCurrency(candidate.price)}</Typography>
      </Flex>
    </List.Item>
  );
}

export const HardwareReplacementModal: React.FC<HardwareReplacementModalProps> = ({
  open,
  currentItem,
  currentQuantity,
  candidates,
  loading,
  onCancel,
  onConfirm,
}) => {
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [quantity, setQuantity] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    // Preselect the current item — most edits are just a quantity change, not an actual swap,
    // so the user shouldn't have to reselect their own item to enable the quantity field.
    // Prefer matching currentItem (which reflects a staged-but-unsaved swap, if any) over the
    // backend's isCurrent flag, which only ever reflects the true saved DB state.
    setSelectedItem(
      candidates.find((c) => c.item === currentItem)?.item ??
        candidates.find((c) => c.isCurrent)?.item ??
        candidates[0]?.item ??
        null
    );
    setQuantity(currentQuantity ?? null);
  }, [open, candidates, currentItem, currentQuantity]);

  const currentCandidate =
    candidates.find((c) => c.item === currentItem) ?? candidates.find((c) => c.isCurrent);

  return (
    <Modal
      open={open}
      footer={null}
      centered
      title="Edit Hardware Item"
      // Confirming here only stages the change locally — it isn't persisted until
      // the details section's own Save button is clicked.
      maskClosable={false}
      keyboard={false}
      onCancel={onCancel}
    >
      <Flex vertical gap="8px">
        {currentItem && (
          <Typography.Text type="secondary">
            Currently: {currentCandidate ? currentCandidate.description : currentItem}
          </Typography.Text>
        )}
        <List
          style={{
            overflowY: "auto",
            width: "100%",
            maxHeight: "calc(70vh - 200px)",
          }}
        >
          {loading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <List.Item key={index} style={{ width: "100%" }}>
                <Skeleton width="100%" height="20px" key={index} />
              </List.Item>
            ))
          ) : (
            candidates.map((candidate) => (
              <SelectableCandidateRow
                key={candidate.item}
                candidate={candidate}
                selected={selectedItem === candidate.item}
                onSelect={() => setSelectedItem(candidate.item)}
              />
            ))
          )}
        </List>

        <Flex justify="space-between" align="center" gap="8px">
          <Typography.Text>Quantity</Typography.Text>
          <InputNumber
            min={1}
            precision={0}
            value={quantity}
            onChange={(v) => setQuantity(typeof v === "number" ? v : null)}
            disabled={loading}
          />
        </Flex>
      </Flex>

      <ModalSaveCancelButtons
        saveButtonText="Confirm"
        disableSave={!selectedItem || quantity == null || quantity < 1 || loading}
        onSave={() => {
          if (selectedItem && quantity != null) onConfirm(selectedItem, quantity);
        }}
        onCancel={onCancel}
      />
    </Modal>
  );
};
